import { createAsyncStoragePersister } from '@tanstack/query-async-storage-persister'
import { MutationCache, QueryCache, QueryClient } from '@tanstack/react-query'
import { hoursToMilliseconds, minutesToMilliseconds } from 'date-fns'
import localforage from 'localforage'
import { toast } from 'sonner'

const DEFAULT_RETRY_COUNT = 0

export const _createQueryClient = () => {
  return new QueryClient({
    queryCache: new QueryCache({}),
    mutationCache: new MutationCache({
      onError: _error => {
        const errorObject = Array.isArray(_error) ? _error[0] : _error
        toast.error(errorObject.message ?? 'Ett fel uppstod')
      },
    }),
    defaultOptions: {
      queries: {
        gcTime: hoursToMilliseconds(24),
        retry: DEFAULT_RETRY_COUNT,
        staleTime: minutesToMilliseconds(1),
        refetchOnWindowFocus: true,
      },
    },
  })
}

export let queryClient: QueryClient

export const createQueryClient = () => {
  if (queryClient) return queryClient
  queryClient = _createQueryClient()

  return queryClient
}

let queryPersister: ReturnType<typeof createAsyncStoragePersister>

// Singleton like `queryClient` above, so that the sign-out path can purge the persisted snapshot —
// clearing only the in-memory cache would leave it to be rehydrated on the next load.
export const createQueryPersister = () => {
  if (queryPersister) return queryPersister
  queryPersister = createAsyncStoragePersister({ storage: localforage })

  return queryPersister
}

/**
 * Drops every trace of the signed-in session's data: in-flight requests, the in-memory cache and the
 * persisted snapshot on disk. Nothing this app fetches is meaningful without a signed-in user, so
 * sign-out discards all of it rather than leaving it to expire on its own (`gcTime` is 24 hours).
 *
 * The order matters: cancelling first stops a fetch already on the wire from repopulating the cache
 * after it has been cleared, and removing the persisted client last means the persister's own
 * throttled write-back can only ever write the already-empty cache.
 */
export const resetQueryCache = async () => {
  await queryClient.cancelQueries()
  queryClient.clear()
  await createQueryPersister().removeClient()
}
